import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { DialogButtons } from '@/components-new/Dialog/styles';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { Input } from '@/components/form/fields/Input/Input';
import { Notification } from '@/components/Notification/Notification';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { VaultAccountType } from '@/types/myVaultTypes';
import { sendVaultConfirmationCode, updateVaultAccountStatus } from '../../api/api';

type ConfirmVaultAccountModalType = {
  isOpen: boolean;
  account: VaultAccountType;
  onClose: () => void;
  onSuccess: () => void;
};

export const ConfirmVaultAccountModal = ({ isOpen, account, onClose, onSuccess }: ConfirmVaultAccountModalType) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [sentConfirmation, setSentConfirmation] = useState(false);

  const { mutate: sendConfirmationCode, isPending: isSendConfirmationCodePending } = useMutation({
    mutationKey: ['send_confirmation_code'],
    mutationFn: () => sendVaultConfirmationCode(account.id),
    onSuccess(response) {
      if (response?.error) return setError(getResponseError(response.error));

      setSentConfirmation(true);
      showSnackbar('We texted a 6-digit code to your phone.');
    },
  });

  const { mutate, isPending } = useMutation({
    mutationKey: ['confirm_vault_account'],
    mutationFn: () => updateVaultAccountStatus(account.id, { status: 'confirmed', code: code.trim() }),
    onSuccess(response) {
      if (response?.error) return setError(getResponseError(response.error));

      showSnackbar('Your Vault Account is confirmed.');
      onSuccess();
      onClose();
    },
  });

  return (
    <Dialog isOpen={isOpen} header='Confirm this account' width='560px' onClose={onClose}>
      <Box display='flex' flexDirection='column' rowGap='14px'>
        <Typography variant='bodySmall'>
          You are confirming that the instructions stored for <b>{account.nickname}</b> ({account.bank_name}{' '}
          {account.masked_number}, record v{account.record_version}) are correct.
          <br />
          Confirmed accounts can be used for insured transactions and ACH.
        </Typography>
        <Typography variant='bodySmall2'>The confirmation is written to the audit log.</Typography>

        {!sentConfirmation && (
          <ButtonNew onClick={() => sendConfirmationCode()} disabled={isSendConfirmationCodePending}>
            Send verification code
          </ButtonNew>
        )}

        {sentConfirmation && (
          <Input
            name='confirmation_code'
            label='Your 6-digit authentication code'
            placeholder='123456'
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete='one-time-code'
          />
        )}

        {error && <Notification variant='error'>{error}</Notification>}
        <DialogButtons>
          <ButtonNew size='medium' variant='secondary' onClick={onClose}>
            Close
          </ButtonNew>
          {sentConfirmation && (
            <ButtonNew size='medium' disabled={code.trim().length < 6} isLoading={isPending} onClick={() => mutate()}>
              Confirm account
            </ButtonNew>
          )}
        </DialogButtons>
      </Box>
    </Dialog>
  );
};
